import type { RegionFacts } from "../../lib/api/lidoo.functions";
import { fill, useI18n } from "../../lib/i18n";
import { shortDate } from "../../lib/place";

// Answers built from the database, not written by hand.
//
// "What does a sunbed cost on the Amalfi Coast" is a question people put to an
// answer engine, and today no page anywhere answers it with a figure. We have
// 147 published rates that nobody else has collected, which makes this the one
// place on the site where we hold original data.
//
// Two rules, and they are what separate a citable answer from a marketing
// claim. Every number carries its sample size, so "EUR 10 to EUR 250 across the
// 12 clubs that publish a rate" can be checked and "sunbeds cost EUR 72"
// cannot. And every answer stands on its own: no "it", no "as mentioned above",
// nothing that breaks when a model quotes the sentence in isolation.
//
// Median rather than mean. Dubai's average of 209 is dragged up by a handful of
// AED 350 day passes and describes no club that actually exists.
export function RegionAnswers({ facts, place }: { facts: RegionFacts; place: string }) {
  const { t, lang } = useI18n();

  const checked = facts.checked ? shortDate(facts.checked, lang) : null;
  const hasPrices = facts.low != null && facts.median != null && facts.high != null;

  const vars = {
    place,
    total: String(facts.clubs),
    towns: String(facts.towns),
    n: String(facts.priced),
    cur: facts.currency === "EUR" ? "EUR" : facts.currency,
    low: String(facts.low ?? ""),
    mid: String(facts.median ?? ""),
    high: String(facts.high ?? ""),
    club: facts.cheapest ?? "",
    rated: String(facts.rated),
    rating: facts.avg_rating != null ? String(facts.avg_rating) : "",
    date: checked ?? "",
  };

  // Only questions this coast can actually answer. A price question on a region
  // where three clubs publish a rate is noise, so it needs at least four.
  const qa: { id: string; q: string; a: string }[] = [
    ...(hasPrices && facts.priced >= 4 && checked
      ? [{ id: "price", q: fill(t("rf_q_price"), vars), a: fill(t("rf_a_price"), vars) }]
      : []),
    ...(facts.rated > 0 && facts.avg_rating != null
      ? [{ id: "count", q: fill(t("rf_q_count"), vars), a: fill(t("rf_a_count"), vars) }]
      : []),
    ...(hasPrices && facts.cheapest && facts.priced >= 4
      ? [{ id: "cheapest", q: fill(t("rf_q_cheap"), vars), a: fill(t("rf_a_cheap"), vars) }]
      : []),
    { id: "booking", q: fill(t("rf_q_book"), vars), a: fill(t("rf_a_book"), vars) },
  ];

  if (qa.length < 2) return null;

  return (
    <section className="mt-12 border-t border-[#1C2430]/15 pt-10">
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            // Freshness is most of what decides whether an answer engine trusts
            // a page. Without a date the schema says nothing about whether these
            // prices are from this season or three years ago.
            ...(facts.checked ? { dateModified: facts.checked } : {}),
            mainEntity: qa.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
      <h2 className="li-serif text-2xl text-[#1C2430] md:text-3xl">
        {fill(t("rf_title"), vars)}
      </h2>
      <dl className="mt-6 divide-y divide-[#1C2430]/10 border-y border-[#1C2430]/10">
        {qa.map((f) => (
          <div key={f.id} id={`faq-${f.id}`} className="scroll-mt-24 py-5">
            <dt className="li-display text-base font-bold tracking-tight text-[#1C2430]">{f.q}</dt>
            <dd className="mt-2 max-w-[68ch] text-sm leading-relaxed text-[#5B6472]">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
